import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Btn, Heading, Modal, Spinner, ErrorState, StatusBadge } from '../../components/platform/ui';
import useAsync from '../../hooks/useAsync';
import { platformAdminApi, errorText } from '../../services/platformApi';
import { Select, Area, DataTable } from './shared';
import { formatDateTime } from '../../lib/pay';

const STATUS_TONE = { new: 'yellow', in_progress: 'blue', done: 'green', rejected: 'gray' };
const KINDS = ['project', 'consultation', 'price'];

const AdminRequests = () => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'ru').slice(0, 2);
  const statusLabel = (s) => (STATUS_TONE[s] ? t(`pl.admin.req.status.${s}`) : s);
  const kindLabel = (k) => (KINDS.includes(k) ? t(`pl.admin.req.kind.${k}`) : k);
  const { data, loading, error, reload } = useAsync(platformAdminApi.requests, []);
  const [sel, setSel] = useState(null);
  const [status, setStatus] = useState('new');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const open = (r) => { setSel(r); setStatus(r.status); setNote(r.admin_note || ''); };
  const save = async () => {
    setBusy(true);
    try { await platformAdminApi.updateRequest(sel.id, { status, admin_note: note }); toast.success(t('pl.admin.req.updated')); setSel(null); reload(); }
    catch (e) { toast.error(errorText(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <Heading level={2} className="text-2xl mb-3">{t('pl.admin.req.title')}</Heading>
      {loading ? <Spinner /> : error ? <ErrorState onRetry={reload} /> : (
        <DataTable empty={t('pl.admin.req.empty')} rows={data} columns={[
          { label: t('pl.admin.req.colDate'), render: (r) => formatDateTime(r.created_at, lang) },
          { label: t('pl.admin.req.colWho'), render: (r) => (<div><b>{r.name}</b><div className="text-xs text-gray-600">{r.email}{r.phone ? ` · ${r.phone}` : ''}</div>{r.clinic && <div className="text-xs">{r.clinic}</div>}</div>) },
          { label: t('pl.admin.req.colType'), render: (r) => `${kindLabel(r.kind)}${r.product_slug ? ` · ${r.product_slug}` : ''}` },
          { label: t('pl.admin.req.colMessage'), render: (r) => <span className="line-clamp-2 max-w-xs inline-block">{r.message}</span> },
          { label: t('pl.admin.req.colStatus'), render: (r) => <StatusBadge tone={STATUS_TONE[r.status]}>{statusLabel(r.status)}</StatusBadge> },
          { label: '', render: (r) => <Btn variant="secondary" onClick={() => open(r)}>{t('pl.admin.req.openBtn')}</Btn> },
        ]} />
      )}
      <Modal open={!!sel} onClose={() => setSel(null)} title={t('pl.admin.req.modal')}
        footer={(<><Btn variant="secondary" onClick={() => setSel(null)}>{t('pl.common.close')}</Btn><Btn onClick={save} loading={busy}>{t('pl.common.save')}</Btn></>)}>
        {sel && (
          <>
            <p className="text-sm text-gray-600 mb-1">{formatDateTime(sel.created_at, lang)}</p>
            <p className="font-bold">{sel.name} · <a className="underline" href={`mailto:${sel.email}`}>{sel.email}</a></p>
            {sel.phone && <p>{sel.phone}</p>}
            {sel.clinic && <p>{sel.clinic}</p>}
            <p className="whitespace-pre-line my-4 p-3 bg-white border border-black/30">{sel.message}</p>
            <Select label={t('pl.admin.req.statusLabel')} value={status} onChange={setStatus} options={Object.keys(STATUS_TONE).map((k) => [k, statusLabel(k)])} />
            <Area label={t('pl.admin.req.note')} rows={3} value={note} onChange={setNote} />
          </>
        )}
      </Modal>
    </div>
  );
};

export default AdminRequests;
